import React from 'react';
import PropTypes from 'prop-types';
import RaisedButton from 'material-ui/RaisedButton';
import Formsy from 'formsy-react';
import moment from 'moment';
import { FormsyDate } from 'formsy-material-ui/lib';
import Checkbox from 'material-ui/Checkbox';
import { Tabs, Tab } from 'material-ui/Tabs';
import TextField from 'material-ui/TextField';
import _ from '../../../../utils/lodash';
import { textFieldStyle, datePickerStyle, common, tabStyles, multiLineText } from '../../../../theme/AllocateBaseTheme';
import { doubleActionPrevent, refSetter, formatNhs } from '../../../../utils/helpers';
import { getDateForApi } from '../../../../utils/dates';
import TreatmentInstanceDialogHeader from '../TreatmentInstanceDialogHeader/TreatmentInstanceDialogHeader';
import TimeSlotsMatrix from '../../../Treatments/TreatmentSchedule/TimeSlots/TimeSlotsMatrix';
import globals from '../../../../globals/communityGlobals';

import { FormsyTime } from '../../../../components/common/Formsy';
import ApiSelector from '../../../../containers/common/selectors/selectorContainer';
import ActivitiesSummary from '../../../../containers/common/ActivitiesSummary/ActivitiesSummaryContainer';
import { teamsEndpoint, activitiesEndpoint, cardTypes } from '../../../../settings/constants';
import recurrenceTypes from '../../../../settings/constants/recurrenceTypes';
import treatmentStatuses from '../../../../settings/constants/treatmentStatuses';
import { treatmentInstanceDialogActions } from '../TreatmentInstanceDialog';

import { Treatment, TreatmentDefaultProps } from '../../../../propTypes/entities/treatment';
import treatmentInstancePropType from '../../../../propTypes/entities/treatmentInstance';
import timeSlotsPackagePropType from '../../../../propTypes/entities/timeSlotsPackage';

const locale = globals.getArea('treatments');
const localeErrors = globals.getArea('error');
const styles = {
  dialogContent: { margin: '0 8px' },
  activitiesRow: { overflow: 'hidden' },
  treatmentDescription: { marginBottom: '1%', color: '#00b5ee' },
  underlineDisabled: { borderWidth: 0, borderColor: '#f5f5f5' },
  specificTime: { opacity: '1' },
  notSpecificTime: { opacity: '0.5' },
  specificTimeDefault: { opacity: '1', margin: '0 auto' },
  notSpecificTimeDefault: { opacity: '0.5', margin: '0 auto' },
  timeSlotCheckbox: { paddingTop: '20px' },
  saveButton: { display: 'flex', flexDirection: 'row', marginTop: '20px' },
  isClosed: { display: true },
  isNotClosed: { display: false },
};

const timeSlotsActions = {
  EDIT: 'EDIT',
};

class TreatmentInstanceDialogEdition extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isSpecific: true,
      canSubmit: true,
    };
    this.validations = {
      isInPresentOrFutureOrExact: this.props.dateAt,
      isSameOrBeforeDate: this.props.occurenceRange.max,
    };
    this.validationErrors = {
      isInPresentOrFutureOrExact: localeErrors.getMsg('cannotBeInThePast'),
      isSameOrBeforeDate: localeErrors.getMsg('treatmentInstanceDatePastNextOccurenceOrEndDate'),
    };
  }

  componentDidMount = () => {
    const { treatmentInstance, timeSlotsMatrixDraft } = this.props;
    const isSpecific = treatmentInstance.isSpecificTime;
    this.setState({ isSpecific });
    if (this.timeSlots && timeSlotsMatrixDraft) {
      this.timeSlots.setValue(timeSlotsMatrixDraft);
    }
  };

  onOccurenceChange = (event, newOccurence) => {
    const { occurenceRange, changeDialogAction, dateAt } = this.props;
    const newOccurenceAt = moment(newOccurence);
    const oldOccurenceAt = moment(dateAt);
    const nowAt = globals.now;

    const isInRange =
      moment(occurenceRange.min).isSameOrBefore(newOccurenceAt) &&
      moment(occurenceRange.max).isSameOrAfter(newOccurenceAt);

    const isTodayOrFuture = nowAt.isSame(newOccurenceAt, 'day') || nowAt.isBefore(newOccurenceAt);

    const isRescheduled = !newOccurenceAt.isSame(oldOccurenceAt, 'day');
    const isValid = isTodayOrFuture && isRescheduled && isInRange;

    if (isValid) {
      // Dirty 'fix' for some formsy/material-ui post-dismount updates
      setTimeout(() => {
        changeDialogAction(treatmentInstanceDialogActions.MOVE, {
          moveAtDraft: newOccurence,
        });
      }, 1);
    } else if (!isRescheduled) {
      // Not rescheduled therefore reset
      changeDialogAction(null, {
        moveAtDraft: undefined,
        moveAt: undefined,
        movedReasonId: undefined,
        movedRiskAssessment: undefined,
      });
    }
  };

  setRef = refSetter.bind(this);

  getOriginalTeams = () => this.props.treatment.orgUnits;

  getTimeSlotsPackage = (treatmentInstance, defaultTimeSlotPackage) =>
    treatmentInstance.timeSlotPackage || defaultTimeSlotPackage;

  getUpdateModelForSpecificTime = (props, model) => {
    const { treatment, treatmentInstanceId, treatmentInstance, moveAt, movedReasonId, movedRiskAssessment } = props;

    return {
      treatmentId: treatment.id,
      treatmentInstanceId,
      isSpecific: true,
      movedReasonId: moveAt ? movedReasonId : undefined,
      movedRiskAssessment: moveAt ? movedRiskAssessment : undefined,
      occurenceDate: moveAt ? getDateForApi(moveAt) : undefined,
      occurenceOriginalDate: getDateForApi(treatmentInstance.occurrence),
      specificTimeFrom: getDateForApi(model.specificTimeFrom),
      orgUnitIds: model.orgUnitId ? [model.orgUnitId] : treatmentInstance.orgUnitIds,
      treatmentInstanceType: treatmentInstance.cardType,
    };
  };

  getUpdateModelForTimeslots = (props, model) => {
    const {
      treatment,
      treatmentInstanceId,
      defaultTimeSlotPackage,
      treatmentInstance,
      moveAt,
      movedReasonId,
      movedRiskAssessment,
    } = props;
    const timeSlotsPackage = this.getTimeSlotsPackage(treatment, defaultTimeSlotPackage);

    return {
      treatmentId: treatment.id,
      treatmentInstanceId,
      isSpecific: false,
      movedReasonId: moveAt ? movedReasonId : undefined,
      movedRiskAssessment: moveAt ? movedRiskAssessment : undefined,
      occurenceDate: moveAt ? getDateForApi(moveAt) : undefined,
      occurenceOriginalDate: getDateForApi(treatmentInstance.occurrence),
      orgUnitIds: model.orgUnitId ? [model.orgUnitId] : treatmentInstance.orgUnitIds,
      timeSlotsMatrix: model.timeSlotsMatrix,
      timeSlotPackageId: timeSlotsPackage ? timeSlotsPackage.id : undefined,
      treatmentInstanceType: treatmentInstance.cardType,
    };
  };

  getSpecificTime = treatmentInstance => {
    const { newSpecificTimeFrom } = this.props;
    const effectiveTimeSlot = treatmentInstance.effectiveTimeSlots ? treatmentInstance.effectiveTimeSlots[0] : null;

    if (newSpecificTimeFrom) return newSpecificTimeFrom;
    return treatmentInstance.isSpecificTime && effectiveTimeSlot ? new Date(effectiveTimeSlot.from) : undefined;
  };

  setNewSpecificTimeFrom = (event, value) => {
    this.props.changeDialogAction(null, {
      newSpecificTimeFrom: value,
    });
  };

  setNewOrgUnitDraft = (event, key, indexes) => {
    this.props.changeDialogAction(null, {
      newOrgUnit: indexes,
    });
  };

  getOrgUnitValue = () => (this.props.newOrgUnit ? { id: this.props.newOrgUnit } : undefined);

  enableButton = () => {
    this.setState({ canSubmit: true });
  };

  disableButton = () => {
    this.setState({ canSubmit: false });
  };

  save = doubleActionPrevent.bind(this)(
    'Save',
    async model => {
      const { save } = this.props;
      const modelUpdated = this.state.isSpecific
        ? this.getUpdateModelForSpecificTime(this.props, model)
        : this.getUpdateModelForTimeslots(this.props, model);

      save(modelUpdated);
    },
    () => {
      const { onClose } = this.props;
      onClose();
    },
  );

  handleFormChange = (values, isChanged) => {
    const { treatmentInstance, changeDialogAction, timeSlotsMatrixDraft } = this.props;
    if (isChanged) {
      if (
        !_.isEqual(values.timeSlotsMatrix, treatmentInstance.selectedTimeSlots) &&
        !_.isEqual(values.timeSlotsMatrix, timeSlotsMatrixDraft)
      ) {
        changeDialogAction(null, { timeSlotsMatrixDraft: values.timeSlotsMatrix });
      }
    } else {
      changeDialogAction(null, { timeSlotsMatrixDraft: undefined });
    }
  };

  render() {
    const {
      treatment,
      onClose,
      defaultTimeSlotPackage,
      dateAt,
      treatmentInstance,
      occurenceRange,
      cardType,
      moveAt,
      canIteract,
    } = this.props;
    const { isSpecific, canSubmit } = this.state;
    const isMasterOrReview = cardType === cardTypes.MASTER || cardType === cardTypes.REVIEW;
    const isMaster = cardType === cardTypes.MASTER;
    const isClosed = treatment.status === treatmentStatuses.CLOSED;
    return (
      <div className="clinicial-planning-dialog">
        <div style={{ marginBottom: '22px' }}>
          <p>{locale.getMsg('patientInfoHeader', [treatment.patientFullName, formatNhs(treatment.nhsNumber)])}</p>
        </div>
        <TreatmentInstanceDialogHeader onClose={onClose}>
          {locale.getMsg('treatmentDetails')}
        </TreatmentInstanceDialogHeader>

        <Formsy.Form
          onValid={this.enableButton}
          onInvalid={this.disableButton}
          onSubmit={this.save}
          onChange={this.handleFormChange}
        >
          <div className="dialog-content-wrapper">
            <Tabs tabItemContainerStyle={tabStyles.tabItemContainerStyle} inkBarStyle={tabStyles.inkBarStyle}>
              <Tab label={locale.getMsg('details')} buttonStyle={tabStyles.buttonStyle}>
                <div className="dialog-content">
                  <div className="row">
                    <div className="col-c12" style={styles.dialogContent}>
                      <ActivitiesSummary activitiesIds={treatmentInstance.activities.map(a => a.id)} />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-c6" style={styles.activitiesRow}>
                      <ApiSelector
                        reduxIdentifier="activities"
                        endpoint={activitiesEndpoint}
                        required
                        multiple
                        floatingLabelText={`${locale.getMsg('activities')}`}
                        name="activitiesIds"
                        value={treatmentInstance.activities}
                        className="select-field readonly"
                        isFetchRequired
                        disabled
                      />
                    </div>
                    <div className="col-c6">
                      <FormsyDate
                        required
                        name="occurenceDate"
                        floatingLabelText={locale.getMsg('rescheduleDate')}
                        className="date-picker"
                        onChange={this.onOccurenceChange}
                        minDate={new Date(occurenceRange.min)}
                        validations={this.validations}
                        validationErrors={this.validationErrors}
                        value={new Date(moveAt || dateAt)}
                        floatingLabelStyle={common.floatingLabelStyle}
                        floatingLabelShrinkStyle={common.floatingLabelShrinkStyle}
                        floatingLabelFocusStyle={common.floatingLabelFocusStyle}
                        underlineStyle={common.underlineStyle}
                        textFieldStyle={datePickerStyle.textFieldStyle}
                        inputStyle={textFieldStyle.inputStyle}
                        disabled={canIteract === false ? true : !isMaster}
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-c6">
                      <ApiSelector
                        reduxIdentifier="teams"
                        endpoint={teamsEndpoint}
                        multiple
                        floatingLabelText={`${locale.getMsg('originalTeams')}:`}
                        value={this.getOriginalTeams()}
                        name="orgUnitId"
                        className="select-field readonly"
                      />
                    </div>
                    <div className="col-c6">
                      <ApiSelector
                        reduxIdentifier="teams"
                        endpoint={teamsEndpoint}
                        floatingLabelText={`${locale.getMsg('newTeam')}`}
                        name="orgUnitId"
                        className="select-field"
                        value={this.getOrgUnitValue()}
                        isFetchRequired
                        onChange={this.setNewOrgUnitDraft}
                        clearable
                        disabled={!canIteract}
                      />
                    </div>
                  </div>
                  <h5 style={styles.treatmentDescription}>{locale.getMsg('treatmentDescription')}</h5>
                  <div className="row">
                    <TextField
                      id="treatmentDescription"
                      className="col-c12 formsytext-css"
                      multiLine
                      rows={5}
                      rowsMax={5}
                      disabled
                      floatingLabelStyle={textFieldStyle.floatingLabelStyle}
                      floatingLabelShrinkStyle={textFieldStyle.floatingLabelShrinkStyle}
                      style={multiLineText.style}
                      inputStyle={multiLineText.inputStyle}
                      underlineDisabledStyle={styles.underlineDisabled}
                      underlineFocusStyle={multiLineText.underlineFocusStyle}
                      value={treatment.description ? treatment.description : ''}
                    />
                  </div>
                </div>
              </Tab>
              <Tab label={locale.getMsg('schedule')} buttonStyle={tabStyles.buttonStyle}>
                <div className="dialog-content">
                  <div className="row">
                    <Checkbox
                      checked={isSpecific}
                      disabled={canIteract === false ? true : !isSpecific || !isMasterOrReview}
                      label={locale.getMsg('specificTime')}
                    />
                  </div>

                  <div className="row" style={isSpecific ? styles.specificTime : styles.notSpecificTime}>
                    <FormsyTime
                      name="specificTimeFrom"
                      floatingLabelText={`${locale.getMsg('treatmentStart')}*`}
                      className="col-c6 time-picker-wrapper"
                      minutesStep={5}
                      required={isSpecific}
                      disabled={canIteract === false ? true : !isSpecific || !isMasterOrReview}
                      value={this.getSpecificTime(treatmentInstance)}
                      floatingLabelStyle={common.floatingLabelStyle}
                      floatingLabelShrinkStyle={common.floatingLabelShrinkStyle}
                      floatingLabelFocusStyle={common.floatingLabelFocusStyle}
                      underlineStyle={common.underlineStyle}
                      textFieldStyle={datePickerStyle.textFieldStyle}
                      inputStyle={textFieldStyle.inputStyle}
                      onChange={this.setNewSpecificTimeFrom}
                    />
                  </div>

                  <Checkbox
                    style={styles.timeSlotCheckbox}
                    checked={!isSpecific}
                    disabled={canIteract === false ? true : isSpecific || !isMasterOrReview}
                    label={locale.getMsg('timeSlot')}
                  />

                  {defaultTimeSlotPackage.timeSlots && (
                    <div
                      className="row table"
                      style={isSpecific ? styles.specificTimeDefault : styles.notSpecificTimeDefault}
                    >
                      <TimeSlotsMatrix
                        ref={this.setRef('timeSlots')}
                        required
                        name="timeSlotsMatrix"
                        value={treatmentInstance.selectedTimeSlots}
                        disabled={canIteract === false ? true : isSpecific || !isMasterOrReview || treatment.isMultiple}
                        timeSlots={this.getTimeSlotsPackage(treatmentInstance, defaultTimeSlotPackage).timeSlots}
                        recurrenceType={recurrenceTypes.DAILY}
                        actionType={timeSlotsActions.EDIT}
                      />
                    </div>
                  )}
                </div>
              </Tab>
            </Tabs>
          </div>
          <div style={styles.saveButton}>
            <div className="save-button">
              <RaisedButton
                primary
                type="submit"
                disabled={canIteract === false ? true : !canSubmit}
                label={locale.getMsg('save')}
                style={isClosed ? styles.isClosed : styles.isNotClosed}
              />
              {isClosed && locale.getMsg('treatmentIsClosed')}
            </div>
          </div>
        </Formsy.Form>
      </div>
    );
  }
}

TreatmentInstanceDialogEdition.defaultProps = {
  treatment: TreatmentDefaultProps,
  treatmentInstance: null,
  moveAt: undefined,
  newOrgUnit: undefined,
  movedRiskAssessment: undefined,
  movedReasonId: undefined,
  newSpecificTimeFrom: undefined,
  timeSlotsMatrixDraft: undefined,
};

TreatmentInstanceDialogEdition.propTypes = {
  treatment: Treatment,
  treatmentInstanceId: PropTypes.number.isRequired,
  treatmentInstance: treatmentInstancePropType,
  timeSlotsMatrixDraft: PropTypes.objectOf(PropTypes.objectOf(PropTypes.bool)),
  onClose: PropTypes.func.isRequired,
  save: PropTypes.func.isRequired,
  defaultTimeSlotPackage: timeSlotsPackagePropType.isRequired,
  cardType: PropTypes.string.isRequired,
  dateAt: PropTypes.string.isRequired,
  moveAt: PropTypes.instanceOf(Date),
  newOrgUnit: PropTypes.number,
  occurenceRange: PropTypes.shape({
    min: PropTypes.string,
    max: PropTypes.string,
  }).isRequired,
  changeDialogAction: PropTypes.func.isRequired,
  movedRiskAssessment: PropTypes.string,
  movedReasonId: PropTypes.number,
  newSpecificTimeFrom: PropTypes.instanceOf(Date),
  canIteract: PropTypes.bool.isRequired,
};

export default TreatmentInstanceDialogEdition;
